// freeNadLife.js
// Free NAD+ for life with a GLP-1 plan, from an offer link. The same rules as the
// gorxpros za checkout (gorxpros lib/free-nad-life.ts):
//
//   ?offer=free-nad-nasal-life&nad=nasal          free NAD+ Nasal (internal_id NAD_NASAL_3000)
//   ?offer=free-nad-injection-life&nad=injection  free NAD+ Injection (internal_id NAD), under the
//   ?offer=free-nad-nasal-life&nad=injection      injection's own offer while the backend runs it,
//                                                 otherwise the free NAD+ Nasal
//
// Both `offer` and `nad` are required, so a typo never ships a product nobody was
// promised. The line is priced 0 and never counted into an amount (Klarna approves
// the amount we ask for), but it has to be on the order, with `offer`, to get the
// NAD+ prescribed and shipped. It comes back as the cart offer utils/cartOffer.js
// already draws and sends for the call-center checkout.
//
// The funnel drops the query on its way to the payment page, so the quiz's first
// page stores the offer for the tab, and clears it on a visit without one.

import axios from 'axios';
import { getProducts, loadCachedProducts, fetchProductsFromServer } from '@/utils/productData';

const NASAL_LIFE = 'free-nad-nasal-life';
const INJECTION_LIFE = 'free-nad-injection-life';
const INTERNAL_ID = { nasal: 'NAD_NASAL_3000', injection: 'NAD' };
// One bottle, or one injection plan, every 3 months: the add-on length both offers
// list in `free_addon_terms`.
const LINE_TERMS = 3;
const STORAGE_KEY = 'rxpros:free-nad-life';
const PRODUCT_IMAGE_BASE_URL = 'https://rxpros.s3.us-east-2.amazonaws.com/public/assets/product_image/webp';

const first = (value) => (Array.isArray(value) ? value[0] : value);
const text = (value) => String(first(value) == null ? '' : first(value)).trim();

/** The offer and form a link names, or null. Injection always goes under the injection's own offer. */
function offerAndForm(offer, nad) {
    const slug = text(offer);
    const form = text(nad).toLowerCase();
    if (slug === NASAL_LIFE && form === 'nasal') return { offer: NASAL_LIFE, form };
    if ((slug === NASAL_LIFE || slug === INJECTION_LIFE) && form === 'injection') return { offer: INJECTION_LIFE, form };
    return null;
}

function readStored() {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        return stored ? offerAndForm(stored.offer, stored.form) : null;
    } catch (e) {
        return null;
    }
}

/** For the funnel's first page: a visit with an offer stores it, a visit without one clears a stale one. */
export function syncFreeNadOffer(query) {
    const offer = query ? offerAndForm(query.offer, query.nad) : null;
    try {
        if (offer) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(offer));
        else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        /* storage blocked (private mode): the order goes through without the offer. */
    }
}

/** The offer the payment page's own link names, else the one the funnel stored. */
export function requestedFreeNadOffer(query) {
    return (query && offerAndForm(query.offer, query.nad)) || readStored();
}

/** The offer as /api/checkout-offers lists it, while it runs. */
function liveOffer(offers, slug) {
    const row = (Array.isArray(offers) ? offers : []).find((o) => o && o.slug === slug);
    if (!row) return null;
    if (row.valid_upto && !(new Date(row.valid_upto).getTime() >= Date.now())) return null;
    return row;
}

const findIn = (products, internalId) =>
    (Array.isArray(products) ? products : []).find((p) =>
        p && typeof p.internal_id === 'string' && p.internal_id.trim().toUpperCase() === internalId && p.is_active !== false
    ) || null;

/** The catalog product, from App.vue's cached catalog, or a fresh one when the cache lacks it. */
async function findProduct(internalId) {
    if (!getProducts()) loadCachedProducts();
    return findIn(getProducts(), internalId) || findIn(await fetchProductsFromServer().catch(() => null), internalId);
}

/**
 * The cart offer (utils/cartOffer.js shape) for a requested free NAD+, or null for a
 * normal order: no offer asked, the backend does not run it, or the catalog does not
 * sell it. Never throws. A free injection the backend does not run becomes the free
 * Nasal, never an injection billed at its catalog price.
 */
export async function loadFreeNadOffer(baseUrl, requested) {
    if (!requested) return null;
    try {
        const response = await axios.get(`${String(baseUrl || '').replace(/\/+$/, '')}/api/checkout-offers`);
        const offers = response && response.data && response.data.offers;
        const run = requested.offer === INJECTION_LIFE && !liveOffer(offers, INJECTION_LIFE)
            ? { offer: NASAL_LIFE, form: 'nasal' }
            : requested;
        const offer = liveOffer(offers, run.offer);
        const product = offer && await findProduct(INTERNAL_ID[run.form]);
        const id = product && (product.id || product._id);
        if (!id) return null;

        const category = typeof product.category === 'string' ? product.category : (product.category && product.category.primary);
        return {
            slug: run.offer,
            name: offer.name,
            for_life: true,
            choice: 'free',
            // The plan lengths the offer grants on: 3, 6 and 12 months today, so a monthly plan leaves the line off.
            eligible_terms: Object.keys(offer.free_addon_terms || {}).map(Number).filter((n) => n > 0),
            line: {
                id,
                quantity: 1,
                price: 0,
                retail_price: 0,
                name: product.name,
                image: product.image_pre_text ? `${PRODUCT_IMAGE_BASE_URL}/${product.image_pre_text}/1.webp` : '',
                category: category || '',
                main_type: 0,
                stripe_id: product.stripe_id || '',
                kku: product.sku || '',
                subheader: product.brand_name || '',
                terms: LINE_TERMS,
                is_addon: true,
                is_free: true,
                free_for_life: run.offer,
            },
        };
    } catch (error) {
        console.error('[free-nad-life] the free NAD+ offer could not be loaded:', error);
        return null;
    }
}
